import { Link } from 'react-router-dom';
import { getPosts, blogBase, strings, SITE_URL } from '../lib/blog';
import { altLinks, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { TopicFilter, useTopicFilter } from '../components/TopicFilter';
import { Seo } from '../components/Seo';

export default function BlogIndex({ lang = 'en' as Lang }: { lang?: Lang }) {
  const t = strings[lang];
  const posts = getPosts(lang);
  const { topic, select, visible } = useTopicFilter(posts);
  const base = blogBase(lang);

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={t.blogTitle}
        description={t.blogDesc}
        canonical={`${SITE_URL}${base}`}
        lang={lang}
        alternates={altLinks('/blog')}
      />
      <BlogHeader lang={lang} />
      <main className="container mx-auto px-6 py-16 max-w-6xl">
        <div className="relative mb-10">
          <div className="blob -top-24 -left-24 h-72 w-72" aria-hidden="true" />
          <span className="eyebrow-pill relative mb-5">ArmProgress</span>
          <h1 className="relative font-heading text-4xl md:text-6xl font-black text-ink tracking-tight mb-4">{t.blogHeading}</h1>
          <p className="relative text-muted text-lg md:text-xl max-w-2xl">{t.blogTagline}</p>
        </div>
        <TopicFilter lang={lang} posts={posts} topic={topic} onSelect={select} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {visible.map((p) => (
            <Link
              key={p.slug}
              to={`${base}/${p.slug}`}
              className="group card-float is-hoverable flex flex-col overflow-hidden"
            >
              <img
                src={p.cover}
                alt={p.title}
                loading="lazy"
                className={
                  p.coverFit === 'contain'
                    ? 'w-full aspect-[1200/630] object-contain bg-elevated py-2'
                    : 'w-full aspect-[1200/630] object-cover'
                }
              />
              <div className="flex flex-1 flex-col p-5">
                <h2 className="font-heading text-lg font-black text-ink group-hover:text-[var(--brand-ink)] transition-colors mb-2 leading-snug line-clamp-2">
                  {p.title}
                </h2>
                <p className="text-muted text-sm mb-4 line-clamp-3">{p.description}</p>
                <span className="mt-auto inline-flex w-fit rounded-full bg-elevated px-2.5 py-1 text-faint text-xs font-semibold">
                  {p.date} · {p.readingTime} {t.minRead}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </main>
      <Footer lang={lang} />
    </div>
  );
}
